/*
 * This is a relatively small abstraction that's ripe for open sourcing.
 * Documentation is in the README.md
 */
import React from 'react'
import {PropTypes} from './react-compat'
import withTheme from './with-theme'
import getGlamorClassName from './get-glamor-classname'

export default createGlamorous

function createGlamorous(splitProps) {
  return glamorous

  /**
  * This is the main export and the function that people
  * interact with most directly.
  *
  * It accepts a component which can be a string or
  * a React Component and returns
  * a "glamorousComponentFactory"
  * @param {String|ReactComponent} comp the component to render
  * @param {Object} options helpful info for the GlamorousComponents
  * @return {Function} the glamorousComponentFactory
  */
  function glamorous(
    comp,
    {
      rootEl,
      displayName,
      shouldClassNameUpdate,
      forwardProps = [],
      propsAreCssOverrides = comp.propsAreCssOverrides,
      withProps: basePropsToApply,
    } = {},
  ) {
    return glamorousComponentFactory

    /**
     * This returns a React Component that renders the comp (closure)
     * with a className based on the given glamor styles object(s)
     * @param {...Object|Function} styles the styles to create with glamor.
     *   If any of these are functions, they are invoked with the component
     *   props and the return value is used.
     * @return {ReactComponent} the ReactComponent function
     */
    function glamorousComponentFactory(...styles) {
      /**
       * This is a component which will render the comp (closure)
       * with the glamorous styles (closure). Forwards any valid
       * props to the underlying component.
       */
      const GlamorousComponent = withTheme(
        (props, context) => {
          props = getPropsToApply(
            GlamorousComponent.propsToApply,
            {},
            props,
            context,
          )
          const updateClassName = shouldUpdate(props, context)

          const {toForward, cssOverrides, cssProp} = splitProps(
            props,
            GlamorousComponent,
          )

          // create className to apply
          GlamorousComponent.className = updateClassName ?
            getGlamorClassName({
              styles: GlamorousComponent.styles,
              props,
              cssOverrides,
              cssProp,
              context,
              displayName: GlamorousComponent.displayName,
            }) :
            GlamorousComponent.className

          return React.createElement(GlamorousComponent.comp, {
            ref: props.innerRef,
            ...toForward,
            className: GlamorousComponent.className,
          })
        },
        {noWarn: true, createElement: false},
      )

      GlamorousComponent.propTypes = {
        className: PropTypes.string,
        cssOverrides: PropTypes.object,
        innerRef: PropTypes.func,
        glam: PropTypes.object,
      }

      function withComponent(newComp, options = {}) {
        return glamorous(newComp, {
          forwardProps: GlamorousComponent.forwardProps,
          ...options,
        })(GlamorousComponent.styles)
      }

      function withProps(...propsToApply) {
        return glamorous(GlamorousComponent, {withProps: propsToApply})()
      }

      function shouldUpdate(props, context) {
        // exiting early so components which do not use this
        // optimization are not penalized by hanging onto
        // references to previous props and context
        if (!shouldClassNameUpdate) {
          return true
        }
        let update = true
        if (GlamorousComponent.previous) {
          if (
            !shouldClassNameUpdate(
              props,
              GlamorousComponent.previous.props,
              context,
              GlamorousComponent.previous.context,
            )
          ) {
            update = false
          }
        }
        GlamorousComponent.previous = {props, context}
        return update
      }

      Object.assign(
        GlamorousComponent,
        getGlamorousComponentMetadata({
          comp,
          styles,
          rootEl,
          forwardProps,
          displayName,
          propsToApply: basePropsToApply,
        }),
        {
          withComponent,
          isGlamorousComponent: true,
          previous: null,
          propsAreCssOverrides,
          withProps,
        },
      )
      return GlamorousComponent
    }
  }

  function getGlamorousComponentMetadata({
    comp,
    styles,
    rootEl,
    forwardProps,
    displayName,
    propsToApply: basePropsToApply,
  }) {
    const componentsComp = comp.comp ? comp.comp : comp
    const propsToApply = comp.propsToApply ?
      [...comp.propsToApply, ...arrayify(basePropsToApply)] :
      arrayify(basePropsToApply)
    return {
      // join styles together (for anyone doing: glamorous(glamorous.a({}), {}))
      styles: when(comp.styles, styles),
      // keep track of the ultimate rootEl to render (we never
      // actually render anything but
      // the base component, even when people wrap a glamorous
      // component in glamorous
      comp: componentsComp,
      rootEl: rootEl || componentsComp,
      // join forwardProps (for anyone doing: glamorous(glamorous.a({}), {}))
      forwardProps: when(comp.forwardProps, forwardProps),
      // set the displayName to something that's slightly more
      // helpful than `GlamorousComponent` :)
      displayName: displayName || `glamorous(${getDisplayName(comp)})`,
      // these are props that should be applied to the component at render time
      propsToApply,
    }
  }
}

/**
 * reduces the propsToApply given to a single props object
 * @param {Array} propsToApply an array of propsToApply objects:
 *   - object
 *   - array of propsToApply items
 *   - function that accepts the accumulated props and the context
 * @param {Object} accumulator an object to apply props onto
 * @param {Object} props the props that should ultimately take precedence
 * @param {*} context the context object
 * @return {Object} the reduced props
 */
function getPropsToApply(propsToApply, accumulator, props, context) {
  // using forEach rather than reduce here because the reduce solution
  // effectively did the same thing because we manipulate the `accumulator`
  propsToApply.forEach(propsToApplyItem => {
    if (typeof propsToApplyItem === 'function') {
      return Object.assign(
        accumulator,
        propsToApplyItem(Object.assign({}, accumulator, props), context),
      )
    } else if (Array.isArray(propsToApplyItem)) {
      return Object.assign(
        accumulator,
        getPropsToApply(propsToApplyItem, accumulator, props, context),
      )
    }
    return Object.assign(accumulator, propsToApplyItem)
  })
  // props wins
  return Object.assign(accumulator, props)
}

function arrayify(x = []) {
  return Array.isArray(x) ? x : [x]
}

function when(comp, prop) {
  return comp ? comp.concat(prop) : prop
}

function getDisplayName(comp) {
  return typeof comp === 'string' ?
    comp :
    comp.displayName || comp.name || 'unknown'
}
